import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Search, Play, X, RefreshCw } from 'lucide-react';
import { adminFetch, fmtNum, fmtRelative } from './api';
import { useAdminAuth } from './AuthContext';
import { PageHeader, EmptyState, ErrorBlock, StatusPill, Section } from './widgets';

const REGIONS = [
  { value: 'all', label: 'All (US + TX + Houston, deduped)' },
  { value: 'us',  label: 'Top US cities' },
  { value: 'tx',  label: 'Top Texas cities' },
  { value: 'hou', label: 'Greater Houston' },
];

export default function AdminDiscover() {
  const { user } = useAdminAuth();
  const [industries, setIndustries] = useState(null);
  const [jobs, setJobs] = useState(null);
  const [error, setError] = useState(null);
  const [showAll, setShowAll] = useState(false);
  const [query, setQuery] = useState('');
  const [region, setRegion] = useState('all');
  const [topN, setTopN] = useState('1000');
  const [limitPerCity, setLimitPerCity] = useState('5000');
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);

  useEffect(() => { document.title = 'Sudco Admin — Discover'; }, []);

  const load = useCallback(async () => {
    setError(null);
    try {
      const [ind, j] = await Promise.all([
        adminFetch('/api/admin-stats/industries'),
        adminFetch('/api/admin-stats/discover-jobs?limit=10'),
      ]);
      setIndustries(ind);
      setJobs(j.jobs);
    } catch (err) {
      setError(err);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // While any job is running, refresh every 5s.
  const hasActive = jobs && jobs.some((j) => j.status === 'queued' || j.status === 'running');
  useEffect(() => {
    if (!hasActive) return undefined;
    const t = setInterval(load, 5000);
    return () => clearInterval(t);
  }, [hasActive, load]);

  const visibleIndustries = useMemo(() => {
    if (!industries) return [];
    return showAll
      ? industries.industries
      : industries.industries.filter((i) => i.total_runs === 0);
  }, [industries, showAll]);

  const clamp = (raw, min, max, fallback) => {
    const n = parseInt(raw, 10);
    if (!Number.isFinite(n)) return fallback;
    return Math.min(max, Math.max(min, n));
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!query) return;
    const top = clamp(topN, 1, 2000, 1000);
    const lpc = clamp(limitPerCity, 1, 50000, 5000);
    setTopN(String(top));
    setLimitPerCity(String(lpc));
    setSubmitError(null);
    setSubmitting(true);
    try {
      await adminFetch('/api/admin-stats/discover-jobs', {
        method: 'POST',
        body: { query, region, top, limit_per_city: lpc },
      });
      setQuery('');
      load();
    } catch (err) {
      setSubmitError(err);
    } finally {
      setSubmitting(false);
    }
  };

  const cancel = async (job) => {
    if (!window.confirm(`Stop discover job for "${job.query}"?`)) return;
    try {
      await adminFetch(`/api/admin-stats/discover-jobs/${job.id}`, { method: 'DELETE' });
      load();
    } catch (err) {
      alert(err.message);
    }
  };

  if (error) return <ErrorBlock error={error} onRetry={load} />;
  if (!industries || !jobs) return <div className="text-text-muted">Loading…</div>;

  const isAdmin = user.role === 'admin';

  return (
    <>
      <PageHeader
        title="Discover"
        subtitle="Run a Foursquare sweep for an industry across many cities."
        right={
          <button onClick={load} className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 bg-surface border border-border rounded-lg hover:bg-surface-2">
            <RefreshCw size={12} /> Refresh
          </button>
        }
      />

      <Section title="Start a sweep">
        {!isAdmin && (
          <div className="rounded-xl border border-border bg-surface p-4 text-sm text-text-muted mb-4">
            Only admins can start discover jobs. You can watch progress below.
          </div>
        )}

        <form onSubmit={submit} className="rounded-xl border border-border bg-surface p-4 space-y-3">
          <div className="grid md:grid-cols-2 gap-3">
            <div className="md:col-span-2">
              <label className="block text-xs uppercase tracking-wide text-text-muted mb-1">Industry</label>
              <select
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                disabled={!isAdmin}
                className="w-full bg-bg border border-border rounded-lg px-3 py-2"
                required
              >
                <option value="">Select an industry…</option>
                {visibleIndustries.map((i) => (
                  <option key={i.query} value={i.query} disabled={i.currently_running}>
                    {i.query} {i.total_runs > 0 ? `(ran ${i.total_runs}x)` : ''} {i.currently_running ? '— running…' : ''}
                  </option>
                ))}
              </select>
              <label className="mt-2 inline-flex items-center gap-2 text-xs text-text-muted">
                <input
                  type="checkbox"
                  checked={showAll}
                  onChange={(e) => setShowAll(e.target.checked)}
                  className="accent-accent"
                />
                Show all industries (including already-run)
              </label>
            </div>
          </div>

          <div className="grid md:grid-cols-3 gap-3">
            <div>
              <label className="block text-xs uppercase tracking-wide text-text-muted mb-1">Region</label>
              <select
                value={region}
                onChange={(e) => setRegion(e.target.value)}
                disabled={!isAdmin}
                className="w-full bg-bg border border-border rounded-lg px-3 py-2"
              >
                {REGIONS.map((r) => <option key={r.value} value={r.value}>{r.label}</option>)}
              </select>
            </div>

            <div>
              <label className="block text-xs uppercase tracking-wide text-text-muted mb-1">Cities (top N)</label>
              <input
                type="number"
                min={1}
                max={2000}
                value={topN}
                onChange={(e) => setTopN(e.target.value)}
                onBlur={() => setTopN(String(clamp(topN, 1, 2000, 1000)))}
                disabled={!isAdmin}
                className="w-full bg-bg border border-border rounded-lg px-3 py-2"
              />
              <div className="mt-1 text-[11px] text-text-muted">All region: ~1045 unique cities (Houston-metro first, then TX, then US)</div>
            </div>

            <div>
              <label className="block text-xs uppercase tracking-wide text-text-muted mb-1">Per-city limit</label>
              <input
                type="number"
                min={1}
                max={50000}
                value={limitPerCity}
                onChange={(e) => setLimitPerCity(e.target.value)}
                onBlur={() => setLimitPerCity(String(clamp(limitPerCity, 1, 50000, 5000)))}
                disabled={!isAdmin}
                className="w-full bg-bg border border-border rounded-lg px-3 py-2"
              />
              <div className="mt-1 text-[11px] text-text-muted">5000 ≈ everything Foursquare returns</div>
            </div>
          </div>

          {submitError && (
            <div className="text-sm text-red-400 bg-red-500/10 border border-red-500/30 rounded-lg px-3 py-2">
              {submitError.message}
            </div>
          )}

          <button
            type="submit"
            disabled={!isAdmin || submitting || !query}
            className="inline-flex items-center gap-1.5 bg-gradient-brand text-white text-sm px-4 py-2 rounded-lg hover:shadow-glow transition-shadow disabled:opacity-60"
          >
            <Play size={14} />
            {submitting ? 'Starting…' : 'Start sweep'}
          </button>
        </form>

        <div className="mt-3 text-xs text-text-muted">
          {industries.industries.length} industries total · {industries.industries.filter((i) => i.total_runs === 0).length} not yet run
          {industries.active_job_id && (
            <span className="ml-2 text-amber-400">
              · A job is currently running (only one at a time).
            </span>
          )}
        </div>
      </Section>

      <Section title="Recent jobs">
        {jobs.length === 0 ? (
          <EmptyState title="No discover jobs yet" hint="Start a sweep above to populate prospects." />
        ) : (
          <div className="space-y-3">
            {jobs.map((j) => <JobCard key={j.id} job={j} onCancel={isAdmin ? cancel : null} />)}
          </div>
        )}
      </Section>
    </>
  );
}

function JobCard({ job, onCancel }) {
  const [expanded, setExpanded] = useState(false);
  const skipped = job.skipped_recent || 0;
  const total = Math.max((job.top_n || 0) - skipped, 1);
  const done = job.cities_done || 0;
  const pct = Math.min(100, (done / total) * 100);
  const tone = job.status === 'running' ? 'sent'
    : job.status === 'completed' ? 'replied'
    : job.status === 'queued' ? 'followup'
    : job.status === 'failed' ? 'bounced'
    : 'neutral';

  return (
    <div className="rounded-xl border border-border bg-surface p-4">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-3 min-w-0">
          <Search size={16} className="text-text-muted shrink-0" />
          <div className="min-w-0">
            <div className="font-medium text-text truncate">{job.query}</div>
            <div className="text-xs text-text-muted">
              {job.region} · top {fmtNum(job.top_n)} · cap {fmtNum(job.limit_per_city || 5000)}/city · {fmtRelative(job.started_at || job.created_at)}
              {job.created_by_email && ` · by ${job.created_by_email}`}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <StatusPill tone={tone}>{job.status}</StatusPill>
          {onCancel && job.status === 'running' && (
            <button
              onClick={() => onCancel(job)}
              className="inline-flex items-center gap-1 text-xs text-red-400 hover:text-red-300 px-2 py-1 rounded border border-red-500/30 bg-red-500/10"
            >
              <X size={12} /> Stop
            </button>
          )}
        </div>
      </div>

      <div className="mt-3">
        <div className="h-2 rounded-full bg-surface-2 overflow-hidden">
          <div
            className={`h-full transition-all duration-500 ${
              job.status === 'failed' ? 'bg-red-500'
              : job.status === 'completed' ? 'bg-emerald-500'
              : 'bg-gradient-brand'
            }`}
            style={{ width: `${pct}%` }}
          />
        </div>
        <div className="flex justify-between mt-1.5 text-xs text-text-muted">
          <span>
            {fmtNum(done)} / {fmtNum(total)} cities
            {skipped > 0 && (
              <span className="ml-1 text-text-muted">
                ({fmtNum(skipped)} of {fmtNum(job.top_n)} pre-filtered)
              </span>
            )}
          </span>
          <span>{fmtNum(job.prospects_stored_live)} prospects stored</span>
        </div>
      </div>

      {job.error && (
        <div className="mt-3 text-sm text-red-400 bg-red-500/10 border border-red-500/30 rounded-lg px-3 py-2">
          {job.error}
        </div>
      )}

      {(job.log_tail || job.status === 'running') && (
        <button
          onClick={() => setExpanded((v) => !v)}
          className="mt-3 text-xs text-text-muted hover:text-text"
        >
          {expanded ? 'Hide log' : 'Show log tail'}
        </button>
      )}
      {expanded && (
        <pre className="mt-2 text-xs bg-bg border border-border rounded-lg p-3 max-h-60 overflow-auto whitespace-pre-wrap text-text-muted">
{job.log_tail || '(no output yet)'}
        </pre>
      )}
    </div>
  );
}
